import React, { useContext, useState } from 'react';
import { getCompanyName, showToast } from '../../../utils';
import moment from 'moment';
import { makeGetCall, makePostCall } from '../../../effects/requests';
import { endPoints } from '../../../utils/endpoints';
import { ApiResponseType } from '../../../types';
import { MyContext } from '../../../context';
import PermissionPill from '../../../global/ui/PermissionPill';
import ExpiryDatePicker from '../../../global/ui/ExpiryDatePicker';
import { ACCESS_LEVEL_OPTIONS, LOCATION_INFO_OPTIONS } from '../../../data/data';

type InviteTab = 'email' | 'crew';

type AccessRole =
    | 'A'
    | 'M'
    | 'V'

type LocationInfo =
    | 'Yes'
    | 'No'

interface Collaborator {
    id: string;
    email: string;
    accessLevel: AccessRole;
    locationAccess: LocationInfo;
    expires: string;
}

interface CollabRowProps {
    collab: Collaborator;
    onAccessLevelChange: (id: string, accessLevel: AccessRole) => void;
    onLocationAccessChange: (id: string, locationAccess: LocationInfo) => void;
    onExpiryChange: (id: string, expires: string) => void;
    onRemove: (id: string) => void;
    onEdit: (id: string) => void;
}

const CollabRow: React.FC<CollabRowProps> = ({
    collab,
    onAccessLevelChange,
    onLocationAccessChange,
    onExpiryChange,
    onRemove,
    onEdit,
}) => {
    return (
        <div className="ac-row">
            {/* Email */}
            <span className="ac-row__email">{collab?.email}</span>

            <div className="ac-row__controls">

                {/* Access Level */}
                <PermissionPill
                    value={collab?.accessLevel}
                    options={ACCESS_LEVEL_OPTIONS}
                    onChange={(value: any) => onAccessLevelChange(collab.id, value as AccessRole)}
                />

                {/* Location Access */}
                <PermissionPill
                    value={collab.locationAccess}
                    options={LOCATION_INFO_OPTIONS}
                    onChange={(value: any) => onLocationAccessChange(collab.id, value as LocationInfo)}
                />

                {/* Expiry */}
                <ExpiryDatePicker
                    value={collab.expires}
                    onChange={(value: any) => onExpiryChange(collab.id, value)}
                />

                {/* Remove */}
                <button
                    type="button"
                    className="ac-row__icon-btn ac-row__icon-btn--remove"
                    onClick={() => onRemove(collab.id)}
                    aria-label="Remove"
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                    >
                        <line
                            x1="18"
                            y1="6"
                            x2="6"
                            y2="18"
                        />
                        <line
                            x1="6"
                            y1="6"
                            x2="18"
                            y2="18"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
};

interface AddCollaboratorModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedLoc: any
}

const LocationSharing: React.FC<
    AddCollaboratorModalProps
> = ({ isOpen, onClose, selectedLoc }) => {

    const { userData } = useContext(MyContext)

    const [tab, setTab] = useState<InviteTab>('email');

    const [emailInput, setEmailInput] = useState('');

    const [collabs, setCollabs] = useState<Collaborator[]>([]);

    if (!isOpen) {
        return null;
    }

    const addCollabs = () => {
        const email = emailInput?.trim();

        if (!email) {
            showToast('Please enter email.', 'error');
            return;
        }

        const alreadyExists = collabs?.some((collab) => collab?.email?.toLowerCase() === email?.toLowerCase());

        if (alreadyExists) {
            showToast('Email already exists.', 'error');
            return;
        }

        makeGetCall(endPoints.USERLIST, { search: email, production_company_name: getCompanyName(userData), user_type: 'C' }).then((res: ApiResponseType) => {
            if (res?.status?.code == 200) {
                if (res?.results?.length > 0) {
                    const newCollaborator: Collaborator = {
                        id: Date.now().toString(),
                        email: email,
                        accessLevel: 'A',
                        locationAccess: 'Yes',
                        expires: 'Never',
                    };

                    setCollabs((prev) => [
                        ...prev,
                        newCollaborator,
                    ]);

                    setEmailInput('');
                    return
                }
                showToast('User does not belong to this production.', 'error')
            }
        }).catch(() => showToast('User does not belong to this production.', 'error'))

    };

    const handleAccessLevelChange = (
        id: string,
        accessLevel: AccessRole
    ) => {
        setCollabs((prev) =>
            prev?.map((collab) =>
                collab?.id === id
                    ? {
                        ...collab,
                        accessLevel,
                    }
                    : collab
            )
        );
    };

    const handleLocationAccessChange = (
        id: string,
        locationAccess: LocationInfo
    ) => {
        setCollabs((prev) =>
            prev?.map((collab) =>
                collab?.id === id
                    ? {
                        ...collab,
                        locationAccess,
                    }
                    : collab
            )
        );
    };

    const handleExpiryChange = (
        id: string,
        expires: string
    ) => {
        setCollabs((prev) =>
            prev?.map((collab) =>
                collab?.id === id
                    ? {
                        ...collab,
                        expires,
                    }
                    : collab
            )
        );
    };

    const handleRemove = (id: string) => {
        setCollabs((prev) => prev?.filter((collab) => collab?.id !== id));
    };

    const handleEdit = (id: string) => {
        const collaborator = collabs?.find((collab) => collab?.id === id);

        if (!collaborator) {
            return;
        }

        setEmailInput(collaborator?.email);
    };

    const handleInvite = () => {
        if (collabs?.length <= 0) {
            showToast('Please add atleast one user to invite.', 'error');
            return
        }

        const result = collabs.map(item => ({
            email: item?.email,
            access_level: item?.accessLevel,
            can_view_location: item?.locationAccess == "Yes",
            expiry_date: item?.expires === "Never" ? null : moment(item.expires, 'DD/MM/YY').format('YYYY-MM-DD')
        }));

        const payload = {
            location_vault_id: selectedLoc?.id,
            users: result
        }

        makePostCall(endPoints.LOCATION_VAULT_ACCESS, payload).then((res: ApiResponseType<any>) => {
            if (res?.status?.code === 200) {
                if (res?.data?.errors?.length > 0) {
                    showToast(res?.data?.errors[0]?.error, 'error');
                    return
                }
                showToast(res?.status?.message, 'success')
            }
            onClose();
        }).catch(onClose)

    }

    const handleCopy = async () => {
        const url = `${process.env?.REACT_APP_WEBSITE_URL}location-folders/${selectedLoc?.id}`
        try {
            await navigator.clipboard.writeText(url);
            showToast("Copied!", 'info');
        } catch (err) {
            console.error("Failed to copy:", err);
        }
    };

    return (
        <div
            className="ac-overlay"
            onClick={onClose}
        >
            <div
                className="ac-modal"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="ac-modal__header">
                    <h2 className="ac-modal__title">
                        Sharing
                    </h2>
                    <div className='ac-invite-btn-wrap'>
                        <button
                            type="button"
                            className="ac-invite-btn"
                            onClick={handleCopy}
                        >
                            <img src='/assets/images/attach-files-dark.svg' alt='close' />
                            Copy Link
                        </button>
                        <button
                            type="button"
                            className="ac-invite-btn"
                            onClick={handleInvite}
                        >
                            <img src='/assets/images/plus-icon.svg' alt='Invite' />
                            Invite
                        </button>
                        <button className="cl-drawer__close" onClick={onClose}>
                            <img src='/assets/images/dismiss.svg' alt='close' />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="ac-tabs">
                    <button
                        type="button"
                        className={`ac-tab${tab === 'email' ? ' ac-tab--active' : ''}`}
                        onClick={() => setTab('email')}
                    >
                        Invite by email
                    </button>

                    {/* <button
                        type="button"
                        className={`ac-tab${tab === 'crew' ? ' ac-tab--active' : ''}`}
                        onClick={() => setTab('crew')}
                    >
                        Invite by crew group
                    </button> */}
                </div>

                {/* Invite */}
                <div className="ac-invite-row">
                    <input
                        className="ac-invite-input"
                        type="email"
                        placeholder="Type email address.."
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                addCollabs();
                            }
                        }}
                    />

                    <button
                        type="button"
                        className="ac-invite-btn"
                        onClick={addCollabs}
                    >
                        <img src='/assets/images/plus-icon.svg' alt='add' />
                        Add
                    </button>
                </div>

                {/* Collaborators */}
                <div className="ac-list">
                    {collabs?.length > 0
                        ?
                        <>
                            {collabs.map((collab) => (
                                <CollabRow
                                    key={collab.id}
                                    collab={collab}
                                    onAccessLevelChange={handleAccessLevelChange}
                                    onLocationAccessChange={handleLocationAccessChange}
                                    onExpiryChange={handleExpiryChange}
                                    onRemove={handleRemove}
                                    onEdit={handleEdit}
                                />
                            ))}
                        </>
                        :
                        <div style={{ minHeight: '30vh' }} className="lg-empty">
                            <img width={150} src='/assets/images/no-results-to-show.png' alt='no data' />
                            <p>No results to show</p>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default LocationSharing;